export function getJsonBracketDepth(text) {
  if (!text || typeof text !== 'string') return null;

  const trimmed = text.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = 0; i < trimmed.length; i += 1) {
    const char = trimmed[i];

    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (char === '\\') {
        escape = true;
        continue;
      }
      if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{' || char === '[') {
      depth += 1;
    } else if (char === '}' || char === ']') {
      depth -= 1;
    }
  }

  return depth;
}

export function isIncompleteJsonDocument(text) {
  if (!text || typeof text !== 'string') return false;

  const trimmed = text.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return false;
  }

  try {
    JSON.parse(text);
    return false;
  } catch {
    return getJsonBracketDepth(text) > 0;
  }
}

export function joinSseDataLines(lines) {
  if (!lines.length) return '';
  return lines.join('\n');
}

export function isUntypedJsonDocument(value) {
  if (value === null || typeof value !== 'object') return false;
  if (!Array.isArray(value) && 'type' in value) return false;
  return true;
}

export function tryParseJsonDocument(raw) {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function formatJsonDocument(value) {
  return JSON.stringify(value, null, 2);
}

/** Event payload is answer text rather than a JSON envelope or the [DONE] marker. */
function isPlainTextPayload(payload) {
  if (typeof payload !== 'string') return false;
  const trimmed = payload.trim();
  if (trimmed === '' || trimmed === '[DONE]') return false;
  return tryParseJsonDocument(payload) === null;
}

export class SseEventAssembler {
  constructor() {
    this.dataLines = [];
    // Some backends (e.g. orchestrator one-shot replies) write a whole multi-line
    // answer under a single `data:` prefix, so its first blank line ends the event
    // and every later line arrives unprefixed. After a plain-text event we keep
    // those lines as continuation text, counting blank lines to restore paragraphs.
    this.textContinuation = null;
  }

  buildMeta() {
    return {
      dataLineCount: this.dataLines.length,
      emptyDataLineCount: this.dataLines.filter((value) => value === '').length,
    };
  }

  pushLine(rawLine) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    if (line === '') {
      if (this.dataLines.length === 0) {
        if (this.textContinuation) {
          this.textContinuation.blankLines += 1;
        }
        return { kind: 'blank' };
      }

      const payload = joinSseDataLines(this.dataLines);
      const meta = this.buildMeta();
      this.dataLines = [];
      // The blank line that ended this event may be a paragraph break in the answer.
      this.textContinuation = isPlainTextPayload(payload) ? { blankLines: 1 } : null;
      return { kind: 'event', payload, meta };
    }

    if (line.startsWith(':')) {
      return { kind: 'comment' };
    }

    if (line.startsWith('data:')) {
      this.textContinuation = null;
      let dataLine = line.slice(5);
      if (dataLine.startsWith(' ')) {
        dataLine = dataLine.slice(1);
      }
      this.dataLines.push(dataLine);
      return { kind: 'data' };
    }

    if (/^(event|id|retry):/.test(line)) {
      this.textContinuation = null;
      return { kind: 'field' };
    }

    const currentPayload = joinSseDataLines(this.dataLines);
    if (this.dataLines.length > 0 && isIncompleteJsonDocument(currentPayload)) {
      const lastIdx = this.dataLines.length - 1;
      this.dataLines[lastIdx] = `${this.dataLines[lastIdx]}\n${line}`;
      return { kind: 'json-continuation' };
    }

    if (this.dataLines.length === 0 && this.textContinuation) {
      const text = `${'\n'.repeat(this.textContinuation.blankLines + 1)}${line}`;
      this.textContinuation.blankLines = 0;
      return { kind: 'text-continuation', text };
    }

    return { kind: 'ignored' };
  }

  flush() {
    if (this.dataLines.length === 0) return null;

    const payload = joinSseDataLines(this.dataLines);
    const meta = this.buildMeta();
    this.dataLines = [];
    return { kind: 'event', payload, meta };
  }
}

export function feedSseChunk(chunk, assembler = new SseEventAssembler()) {
  const events = [];
  const lines = chunk.split('\n');
  const remainder = lines.pop() ?? '';

  for (const rawLine of lines) {
    const result = assembler.pushLine(rawLine);
    if (result.kind === 'event') {
      events.push({ payload: result.payload, meta: result.meta });
    }
  }

  return { events, assembler, remainder };
}

export function parseSseStream(streamText) {
  const assembler = new SseEventAssembler();
  const events = [];

  for (const rawLine of streamText.split('\n')) {
    const result = assembler.pushLine(rawLine);
    if (result.kind === 'event') {
      events.push({ payload: result.payload, meta: result.meta });
    }
  }

  const trailing = assembler.flush();
  if (trailing) {
    events.push({ payload: trailing.payload, meta: trailing.meta });
  }

  return events;
}
